/**
 * Shared jobs data source
 *
 * Serves manually managed jobs when configured, otherwise fetches from the
 * Pinpoint API. Caches the result so multiple consumers (careers list, team
 * job counts) share a single network request.
 *
 * @package Interact_26
 */

import { type CareersConfig, type PinpointApiPosting } from './types';

let cachedPromise: Promise<PinpointApiPosting[]> | null = null;

/**
 * Get postings from the configured source. Manual mode never touches the
 * network and never falls back to the API. Returns a cached promise if
 * already in-flight or resolved, so the request is only made once per load.
 */
export const fetchPostings = (config: CareersConfig): Promise<PinpointApiPosting[]> => {
  if (Array.isArray(config.manualJobs)) {
    return Promise.resolve(config.manualJobs);
  }

  if (!config.apiUrl) {
    return Promise.reject(new Error('No jobs source configured'));
  }

  if (cachedPromise) {
    return cachedPromise;
  }

  cachedPromise = fetch(config.apiUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      const postings: PinpointApiPosting[] = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      return postings;
    })
    .catch((err) => {
      // Reset cache on error so a retry is possible
      cachedPromise = null;
      throw err;
    });

  return cachedPromise;
};
